import React from 'react';

import {Popover, Table, Tooltip} from 'spectra-logic-ui/components';

import {TargetType} from '@/enum';
import {BPProperties, CloudProperties, Pool, Target, TargetItemDetails, VolumeProperties} from '@/types';
import {isArchival} from '@/storage/archive';
import {createTypeDisplayName} from '@/storage/storage_rows';
import {Dispatch, Store} from 'spectra-logic-ui';
import {fetchResource} from 'spectra-logic-ui/actions';
import {connect} from 'react-redux';
import {isCloud} from '@/storage/cloud';
import {isBP} from '@/storage/on_site_storage';
import {numberToHumanSize} from 'spectra-logic-ui/helpers/number';

type Props = {
  storage: Pool;
  itemDetails?: TargetItemDetails;
  targetTypes?: Target[];
  fetchItemDetails?: Function;
  fetchTargetTypes?: Function;
}

export const cloneRestoreTooltip = 'The restore in-place option uses internal resources on ' +
  'the archival storage. Without this option, a new clone is created on different storage. Selecting ' +
  'this option may use additional cache space or incur cloud storage fees.';

export const recoverableTooltip = 'Enabling third-party recovery causes additional information'+
  ' to be written, allowing full objects to be generated from the content of the storage.'+
  ' This option impacts performance and increases storage usage.';

const PropertiesDetails = ({storage, fetchItemDetails, fetchTargetTypes, targetTypes, itemDetails}: Props) => {
  React.useEffect(() => {
    if (fetchItemDetails !== undefined) {
      fetchItemDetails();
    }
    if (fetchTargetTypes !== undefined) {
      fetchTargetTypes();
    }
  }, [storage]);

  let owner;
  let policy;
  let partitions;
  let region;
  let url;
  let unsafe;
  let virtualHost;
  let theirClass;
  let classLabel;
  let quota;
  let itemTitle;
  let writeLimit;
  if (storage && storage.target) {
    const targetInfo = targetTypes ? targetTypes.find((t) => t.id === storage.target) : undefined;
    if (targetInfo && targetInfo.name) {
      itemTitle = targetInfo.name;
    }
  }
  if (itemDetails && itemDetails.properties) {
    if (isBP(storage)) {
      const bp = itemDetails.properties as BPProperties;
      if (bp.policy) {
        if (bp.policyName) {
          policy = <Popover anchor={<span>{bp.policyName}</span>}>
            <span>ID: {bp.policy}</span>
          </Popover>;
        } else {
          owner = bp.policy;
        }
      }
      if (bp.partitions) {
        partitions = bp.partitions.join(', ');
      }
    }
    if (isCloud(storage)) {
      let cloud = {} as CloudProperties;
      cloud = itemDetails.properties as CloudProperties;
      if (cloud.owner) {
        owner = cloud.owner;
      }
      if (cloud.region) {
        region = cloud.region;
      }
      if (cloud.endpoint) {
        url = cloud.endpoint;
      }
      if (cloud.unsafe !== undefined) {
        unsafe = cloud.unsafe;
      }
      if (storage.target === TargetType.AZURE) {
        classLabel = 'Azure Tier';
      } else if (storage.target === TargetType.GOOGLE) {
        classLabel = 'Google Class';
      } else {
        classLabel = 'Destination Class';
      }
      if (cloud.theirClass === undefined) {
        theirClass = 'Use Default';
      } else {
        theirClass = cloud.theirClass;
      }
      if (storage.target === TargetType.S3OTHER) {
        virtualHost = cloud.virtualHost;
      }
    }
    if (storage.target === TargetType.VOLUME && itemDetails.quota) {
      const volume = itemDetails.properties as VolumeProperties;
      if (volume.quota) {
        quota = volume.quota;
      }
    }
  }
  if (itemDetails && itemDetails.writeLimit) {
    if (storage.writeLimit) {
      writeLimit = storage.writeLimit + ' MiB/sec';
    } else {
      writeLimit = 'Unlimited';
    }
  }

  // Show the recoverable parameter for all storage types that allow
  // the user to set it.
  const showRecoverable = storage.target !== TargetType.BPLINK;
  const showExport = storage.target !== TargetType.VOLUME &&
    storage.target !== TargetType.BPLINK && storage.target !== TargetType.BLACKPEARL;
  const showCurrentVersion = storage.target === TargetType.S3 || storage.target === TargetType.S3OTHER;
  const showImport = storage.useWith && (storage.target === TargetType.S3 || storage.target === TargetType.S3OTHER);
  return (
    <Table>
      <Table.Body>
        <Table.Row>
          <Table.Cell>ID</Table.Cell>
          <Table.Cell>{storage.id}</Table.Cell>
        </Table.Row>
        <Table.Row>
          <Table.Cell>Status</Table.Cell>
          <Table.Cell>{storage.disabled ? 'Disabled' : storage.status}</Table.Cell>
        </Table.Row>
        <Table.Row>
          <Table.Cell>Storage Class</Table.Cell>
          <Table.Cell>{storage.storageClass}</Table.Cell>
        </Table.Row>
        {showCurrentVersion &&
          <Table.Row>
            <Table.Cell>Current Version Only</Table.Cell>
            <Table.Cell>{storage.currentOnly ? 'Enabled' : 'Disabled'}</Table.Cell>
          </Table.Row>
        }
        {writeLimit &&
          <Table.Row>
            <Table.Cell>Write Limit</Table.Cell>
            <Table.Cell>{writeLimit}</Table.Cell>
          </Table.Row>
        }
        {showExport &&
          <Table.Row>
            <Table.Cell>Export</Table.Cell>
            <Table.Cell>{storage.exporter ? 'Enabled' : 'Disabled'}</Table.Cell>
          </Table.Row>
        }
        {showImport &&
          <Table.Row>
            <Table.Cell>Import Content</Table.Cell>
            <Table.Cell>{storage.importer ? 'Enabled' : 'Disabled'}</Table.Cell>
          </Table.Row>
        }
        {storage.importer &&
          <Table.Row>
            <Table.Cell>Import Notifications</Table.Cell>
            <Table.Cell>{storage.pauseNotifications ? 'Paused' : 'Enabled'}</Table.Cell>
          </Table.Row>
        }
        <Table.Row>
          <Table.Cell>Use With Buckets</Table.Cell>
          <Table.Cell>{storage.useWith && storage.useWith.length > 0 ?
            storage.useWith.join(', ') : 'All buckets'}</Table.Cell>
        </Table.Row>
        {showRecoverable &&
            <Table.Row>
              <Table.Cell>Third-party Recovery<Tooltip>{recoverableTooltip}</Tooltip></Table.Cell>
              <Table.Cell>{storage.recoverable ? 'Enabled' : 'Disabled'}</Table.Cell>
            </Table.Row>
        }
        {isArchival(storage) &&
          <Table.Row>
            <Table.Cell>Restore In-place<Tooltip>{cloneRestoreTooltip}</Tooltip></Table.Cell>
            <Table.Cell>{storage.restoreInPlace ? 'Enabled' : 'Disabled'}</Table.Cell>
          </Table.Row>
        }
        <Table.Row>
          <Table.Cell>Type</Table.Cell>
          <Table.Cell>{createTypeDisplayName(storage)}</Table.Cell>
        </Table.Row>
        {itemTitle && storage.item &&
          <Table.Row>
            <Table.Cell>{itemTitle}</Table.Cell>
            <Table.Cell>{storage.item}</Table.Cell>
          </Table.Row>
        }
        {theirClass &&
          <Table.Row>
            <Table.Cell>{classLabel}</Table.Cell>
            <Table.Cell>{theirClass}</Table.Cell>
          </Table.Row>
        }
        {url &&
          <Table.Row>
            <Table.Cell>Data Path Endpoint</Table.Cell>
            <Table.Cell>{url}</Table.Cell>
          </Table.Row>
        }
        {unsafe !== undefined &&
          <Table.Row>
            <Table.Cell>Skip TLS Verification</Table.Cell>
            <Table.Cell>{unsafe ? 'Yes' : 'No'}</Table.Cell>
          </Table.Row>
        }
        {region &&
          <Table.Row>
            <Table.Cell>Region</Table.Cell>
            <Table.Cell>{region}</Table.Cell>
          </Table.Row>
        }
        {storage.target === TargetType.S3OTHER &&
          <Table.Row>
            <Table.Cell>Addressing Style</Table.Cell>
            <Table.Cell>{virtualHost ? 'Virtual Hosted' : 'Path'}</Table.Cell>
          </Table.Row>
        }
        {owner &&
          <Table.Row>
            <Table.Cell>{storage.target === TargetType.AZURE ? 'Storage Account' : 'Bucket Owner'}</Table.Cell>
            <Table.Cell>{owner}</Table.Cell>
          </Table.Row>
        }
        {policy &&
          <Table.Row>
            <Table.Cell>Data Policy</Table.Cell>
            <Table.Cell>{policy}</Table.Cell>
          </Table.Row>
        }
        {partitions &&
          <Table.Row>
            <Table.Cell>Data Partitions</Table.Cell>
            <Table.Cell>{partitions}</Table.Cell>
          </Table.Row>
        }
        {storage.cautionThreshold &&
            <Table.Row>
              <Table.Cell>Caution Threshold</Table.Cell>
              <Table.Cell>{storage.cautionThreshold}%</Table.Cell>
            </Table.Row>
        }
        {storage.warningThreshold &&
            <Table.Row>
              <Table.Cell>Warning Threshold</Table.Cell>
              <Table.Cell>{storage.warningThreshold}%</Table.Cell>
            </Table.Row>
        }
        {storage.optionalData &&
            <Table.Row>
              <Table.Cell>Optional Data</Table.Cell>
              <Table.Cell>{storage.optionalData}%</Table.Cell>
            </Table.Row>
        }
        {itemDetails !== undefined && itemDetails.quota &&
          <Table.Row>
            <Table.Cell>Quota</Table.Cell>
            <Table.Cell>{quota !== undefined ? numberToHumanSize(quota) : '--'}</Table.Cell>
          </Table.Row>
        }
        {storage.verificationRunning &&
          <Table.Row>
            <Table.Cell>Verification</Table.Cell>
            <Table.Cell>In Progress</Table.Cell>
          </Table.Row>
        }
      </Table.Body>
    </Table>
  );
};

const mapStateToProps = (state: Store, ownProps: Props) => {
  const itemDetails = state.resources['storage/' + ownProps.storage.id + '/item'] || {};
  const targetTypes = state.resources['targets'] || {};
  return {itemDetails: itemDetails.data, targetTypes: targetTypes.data};
};

const mapDispatchToProps = (dispatch: Dispatch, ownProps: Props) => ({
  fetchItemDetails: () => dispatch(fetchResource('storage/' + ownProps.storage.id + '/item')),
  fetchTargetTypes: () => dispatch(fetchResource('targets')),
});

export default connect(mapStateToProps, mapDispatchToProps)(PropertiesDetails);
